<template>
  <div class="dashboard-layout">
    <!-- Sidebar -->
    <Sidebar />

    <!-- Main Content -->
    <div class="main-content">
      <!-- Top Header -->
      <TopHeader pageTitle="Administration" />

      <!-- Page Content -->
      <main class="page-content">
        <!-- Loading State -->
        <div v-if="loading" class="flex justify-center items-center h-screen">
          <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        </div>

        <!-- Error State -->
        <div v-else-if="error" class="p-6">
          <div class="bg-red-50 border border-red-200 rounded-lg p-4">
            <p class="text-red-800">{{ error }}</p>
          </div>
        </div>

        <!-- Dashboard Content -->
        <div v-else class="h-full flex flex-col">
          <!-- Tabs Navigation and Date Filter -->
          <div class="bg-white border-b border-gray-200 sticky top-0 z-10">
            <div class="flex justify-end items-center px-6 py-3">
              <!-- Date Range Filter -->
              <div class="flex items-center gap-3">
                <label class="text-sm font-medium text-gray-600">Filter by:</label>
                <select
                  v-model="dateFilter"
                  @change="applyDateFilter"
                  class="px-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white"
                >
                  <option value="all">All Time</option>
                  <option value="today">Today</option>
                  <option value="week">This Week</option>
                  <option value="month">This Month</option>
                  <option value="quarter">This Quarter</option>
                  <option value="year">This Year</option>
                  <option value="custom">Custom Range</option>
                </select>

                <!-- Custom Date Range -->
                <div v-if="dateFilter === 'custom'" class="flex items-center gap-2">
                  <input
                    type="date"
                    v-model="customStartDate"
                    @change="applyDateFilter"
                    class="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                  <span class="text-gray-500">to</span>
                  <input
                    type="date"
                    v-model="customEndDate"
                    @change="applyDateFilter"
                    class="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>

                <button
                  @click="loadAllData"
                  class="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors flex items-center gap-2"
                >
                  <span>🔄</span> Refresh
                </button>

                <!-- Force Initialize All WhatsApp Instances Button -->
                <button
                  @click="forceInitializeAllInstances"
                  :disabled="initializingInstances"
                  class="px-6 py-2 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg text-sm font-semibold hover:from-green-700 hover:to-emerald-700 transition-all flex items-center gap-2 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span v-if="!initializingInstances">⚡</span>
                  <span v-else class="animate-spin">⏳</span>
                  {{ initializingInstances ? 'Initializing...' : 'Force Initialize All WhatsApp Instances' }}
                </button>
              </div>
            </div>

            <nav class="flex gap-1 p-1 tabs-container rounded-lg mx-6 mb-3 overflow-x-auto">
              <button
                v-for="tab in tabs"
                :key="tab.id"
                @click="activeTab = tab.id"
                :class="[
                  'px-4 py-2 text-sm font-medium transition-all whitespace-nowrap rounded-md tab-item',
                  activeTab === tab.id ? 'active' : ''
                ]"
              >
                {{ tab.label }}
              </button>
            </nav>
          </div>

          <!-- Tab Content -->
          <div class="flex-1 overflow-y-auto">
            <div class="p-6">
              <!-- Overview Tab -->
              <div v-if="activeTab === 'overview'">
                <!-- Date Range Display -->
                <div v-if="dateFilter !== 'all'" class="mb-3 p-2 bg-indigo-50 rounded border border-indigo-200">
                  <p class="text-xs text-indigo-800">
                    <span class="font-semibold">Showing data for:</span>
                    {{ getDateRangeDisplay() }}
                  </p>
                </div>

                <!-- Key Metrics Cards -->
                <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                  <!-- MRR Card -->
                  <div class="bg-white rounded-lg shadow-sm p-4 border-l-4 border-indigo-600">
                    <div class="flex items-center justify-between mb-1">
                      <h3 class="text-xs font-medium text-gray-600">MRR</h3>
                      <span class="text-xl">📈</span>
                    </div>
                    <p class="text-xl font-bold text-gray-900">
                      {{ formatCurrency(overview.mrr?.total || 0) }}
                    </p>
                    <p class="text-xs text-gray-500 mt-0.5">
                      Net: {{ formatCurrency(overview.mrr?.revenue_without_vat || 0) }}
                    </p>
                    <p class="text-xs text-gray-400 mt-0.5">
                      VAT: {{ formatCurrency(overview.mrr?.vat_amount || 0) }}
                    </p>
                  </div>

                  <!-- ARR Card -->
                  <div class="bg-white rounded-lg shadow-sm p-4 border-l-4 border-green-600">
                    <div class="flex items-center justify-between mb-1">
                      <h3 class="text-xs font-medium text-gray-600">ARR</h3>
                      <span class="text-xl">💰</span>
                    </div>
                    <p class="text-xl font-bold text-gray-900">
                      {{ formatCurrency(overview.arr?.total || 0) }}
                    </p>
                    <p class="text-xs text-gray-500 mt-0.5">
                      Net: {{ formatCurrency(overview.arr?.revenue_without_vat || 0) }}
                    </p>
                    <p class="text-xs text-gray-400 mt-0.5">
                      VAT: {{ formatCurrency(overview.arr?.vat_amount || 0) }}
                    </p>
                  </div>

                  <!-- Total Revenue Card -->
                  <div class="bg-white rounded-lg shadow-sm p-4 border-l-4 border-purple-600">
                    <div class="flex items-center justify-between mb-1">
                      <h3 class="text-xs font-medium text-gray-600">Total Revenue</h3>
                      <span class="text-xl">💵</span>
                    </div>
                    <p class="text-xl font-bold text-gray-900">
                      {{ formatCurrency(overview.total_revenue?.total || 0) }}
                    </p>
                    <p class="text-xs text-gray-500 mt-0.5">
                      Net: {{ formatCurrency(overview.total_revenue?.revenue_without_vat || 0) }}
                    </p>
                    <p class="text-xs text-gray-400 mt-0.5">
                      VAT: {{ formatCurrency(overview.total_revenue?.vat_amount || 0) }}
                    </p>
                  </div>

                  <!-- Active Subscriptions Card -->
                  <div class="bg-white rounded-lg shadow-sm p-4 border-l-4 border-blue-600">
                    <div class="flex items-center justify-between mb-1">
                      <h3 class="text-xs font-medium text-gray-600">Active Subscriptions</h3>
                      <span class="text-xl">👥</span>
                    </div>
                    <p class="text-xl font-bold text-gray-900">
                      {{ overview.active_subscriptions || 0 }}
                    </p>
                    <p class="text-xs text-green-600 mt-0.5">
                      +{{ overview.new_customers_this_month || 0 }} new this month
                    </p>
                    <p class="text-xs text-red-600 mt-0.5">
                      -{{ overview.churned_this_month || 0 }} churned
                    </p>
                  </div>
                </div>

                <!-- Expense & Profit Metrics -->
                <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <!-- Monthly Expenses Card -->
                  <div class="bg-white rounded-lg shadow-sm p-4 border-l-4 border-red-600">
                    <div class="flex items-center justify-between mb-1">
                      <h3 class="text-xs font-medium text-gray-600">Monthly Expenses</h3>
                      <span class="text-xl">💸</span>
                    </div>
                    <p class="text-xl font-bold text-red-600">
                      -{{ formatCurrency(expenseBreakdown.total_monthly_expenses || 0) }}
                    </p>
                    <p class="text-xs text-gray-500 mt-0.5">
                      Active recurring costs
                    </p>
                    <button @click="activeTab = 'expenses'" class="text-xs text-indigo-600 hover:text-indigo-800 mt-1 font-medium">
                      View Details →
                    </button>
                  </div>

                  <!-- Net Profit Card -->
                  <div class="bg-white rounded-lg shadow-sm p-4 border-l-4" :class="(overview.mrr?.revenue_without_vat || 0) - (expenseBreakdown.total_monthly_expenses || 0) >= 0 ? 'border-green-600' : 'border-red-600'">
                    <div class="flex items-center justify-between mb-1">
                      <h3 class="text-xs font-medium text-gray-600">Net Profit (This Month)</h3>
                      <span class="text-xl">{{ (overview.mrr?.revenue_without_vat || 0) - (expenseBreakdown.total_monthly_expenses || 0) >= 0 ? '✅' : '⚠️' }}</span>
                    </div>
                    <p class="text-xl font-bold" :class="(overview.mrr?.revenue_without_vat || 0) - (expenseBreakdown.total_monthly_expenses || 0) >= 0 ? 'text-green-600' : 'text-red-600'">
                      {{ formatCurrency((overview.mrr?.revenue_without_vat || 0) - (expenseBreakdown.total_monthly_expenses || 0)) }}
                    </p>
                    <p class="text-xs text-gray-500 mt-0.5">
                      MRR ({{ formatCurrency(overview.mrr?.revenue_without_vat || 0) }}) - Expenses ({{ formatCurrency(expenseBreakdown.total_monthly_expenses || 0) }})
                    </p>
                    <p class="text-xs font-medium mt-1" :class="calculateProfitMargin() >= 0 ? 'text-green-600' : 'text-red-600'">
                      Profit Margin: {{ calculateProfitMargin() }}%
                    </p>
                  </div>
                </div>

                <!-- Secondary Metrics - Compact Single Row -->
                <div class="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
                  <!-- ARPU -->
                  <div class="bg-white rounded-lg shadow-sm p-3">
                    <h3 class="text-xs font-medium text-gray-600 mb-1">ARPU</h3>
                    <p class="text-lg font-bold text-gray-900">
                      {{ formatCurrency(overview.arpu?.with_vat || 0) }}
                    </p>
                    <p class="text-xs text-gray-500">
                      Net: {{ formatCurrency(overview.arpu?.without_vat || 0) }}
                    </p>
                  </div>

                  <!-- Payment Success Rate -->
                  <div class="bg-white rounded-lg shadow-sm p-3">
                    <h3 class="text-xs font-medium text-gray-600 mb-1">Payment Success</h3>
                    <p class="text-lg font-bold text-gray-900">
                      {{ overview.payment_success_rate?.success_rate || 0 }}%
                    </p>
                    <p class="text-xs text-gray-500">
                      {{ overview.payment_success_rate?.successful_payments || 0 }}/{{ overview.payment_success_rate?.total_payments || 0 }} payments
                    </p>
                  </div>

                  <!-- VAT Collected -->
                  <div class="bg-white rounded-lg shadow-sm p-3">
                    <h3 class="text-xs font-medium text-gray-600 mb-1">VAT Collected</h3>
                    <p class="text-lg font-bold text-gray-900">
                      {{ formatCurrency(overview.revenue_breakdown?.vat_collected || 0) }}
                    </p>
                    <p class="text-xs text-gray-500">
                      Total: {{ formatCurrency(overview.revenue_breakdown?.total_collected || 0) }}
                    </p>
                  </div>

                  <!-- WhatsApp Messages -->
                  <div class="bg-white rounded-lg shadow-sm p-3 border-l-4 border-green-600">
                    <div class="flex items-center justify-between mb-1">
                      <h3 class="text-xs font-medium text-gray-600">WhatsApp</h3>
                      <span class="text-lg">💬</span>
                    </div>
                    <p class="text-lg font-bold text-gray-900">
                      {{ whatsappStats.today_messages || 0 }}
                    </p>
                    <p class="text-xs text-gray-500">
                      {{ whatsappStats.week_total || 0 }} this week
                    </p>
                  </div>
                </div>

                <!-- Charts Section -->
                <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <!-- Revenue Trend Chart -->
                  <div class="bg-white rounded-lg shadow-sm p-4">
                    <h3 class="text-sm font-semibold text-gray-900 mb-3">Revenue Trend (12 Months)</h3>
                    <div class="chart-container" style="position: relative; height: 250px;">
                      <canvas ref="revenueTrendChart"></canvas>
                    </div>
                  </div>

                  <!-- Revenue by Plan Chart -->
                  <div class="bg-white rounded-lg shadow-sm p-4">
                    <h3 class="text-sm font-semibold text-gray-900 mb-3">Revenue Distribution by Plan</h3>
                    <div class="grid grid-cols-2 gap-3">
                      <div class="chart-container" style="position: relative; height: 200px;">
                        <canvas ref="revenuePlanChart"></canvas>
                      </div>
                      <div class="flex flex-col justify-center gap-2 text-xs">
                        <div v-for="(plan, index) in overview.revenue_by_plan" :key="plan.plan_id" class="flex items-center justify-between p-2 rounded bg-gray-50">
                          <div class="flex items-center gap-2">
                            <div :style="{ backgroundColor: getPlanColor(index) }" class="w-3 h-3 rounded-full"></div>
                            <span class="font-medium text-gray-900">{{ plan.plan_name }}</span>
                          </div>
                          <div class="text-right">
                            <div class="font-bold text-gray-900">{{ formatCurrency(plan.total_revenue) }}</div>
                            <div class="text-gray-500">{{ plan.payment_count }} payments</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <!-- MRR Chart -->
                  <div class="bg-white rounded-lg shadow-sm p-4">
                    <h3 class="text-sm font-semibold text-gray-900 mb-3">Monthly Recurring Revenue (MRR)</h3>
                    <div class="chart-container" style="position: relative; height: 250px;">
                      <canvas ref="mrrChart"></canvas>
                    </div>
                  </div>

                  <!-- ARR Chart -->
                  <div class="bg-white rounded-lg shadow-sm p-4">
                    <h3 class="text-sm font-semibold text-gray-900 mb-3">Annual Recurring Revenue (ARR)</h3>
                    <div class="chart-container" style="position: relative; height: 250px;">
                      <canvas ref="arrChart"></canvas>
                    </div>
                  </div>

                  <!-- Subscription Growth History -->
                  <div class="bg-white rounded-lg shadow-sm p-4 lg:col-span-2">
                    <h3 class="text-sm font-semibold text-gray-900 mb-3">Subscription Growth (Past 12 Months)</h3>
                    <div class="chart-container" style="position: relative; height: 250px;">
                      <canvas ref="subscriptionGrowthChart"></canvas>
                    </div>
                  </div>

                  <!-- WhatsApp Messages Trend (Past 7 Days) -->
                  <div class="bg-white rounded-lg shadow-sm p-4 lg:col-span-2">
                    <div class="flex items-center justify-between mb-3">
                      <h3 class="text-sm font-semibold text-gray-900">WhatsApp Messages (Past 7 Days)</h3>
                      <div class="text-right">
                        <p class="text-2xl font-bold text-green-600">{{ whatsappStats.week_total || 0 }}</p>
                        <p class="text-xs text-gray-500">Total Messages</p>
                      </div>
                    </div>
                    <div class="chart-container" style="position: relative; height: 250px;">
                      <canvas ref="whatsappMessagesChart"></canvas>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Revenue by Plan Tab -->
              <div v-if="activeTab === 'plans'" class="overflow-x-auto">
                <h3 class="text-lg font-semibold text-gray-900 mb-4">Revenue by Plan</h3>
                <table class="min-w-full divide-y divide-gray-200">
                  <thead class="bg-gray-50">
                    <tr>
                      <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Plan</th>
                      <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Payments</th>
                      <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Revenue</th>
                      <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Net Revenue</th>
                      <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">VAT</th>
                    </tr>
                  </thead>
                  <tbody class="bg-white divide-y divide-gray-200">
                    <tr v-for="plan in overview.revenue_by_plan" :key="plan.plan_id">
                      <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{{ plan.plan_name }}</td>
                      <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{{ plan.payment_count }}</td>
                      <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{{ formatCurrency(plan.total_revenue) }}</td>
                      <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{{ formatCurrency(plan.revenue_without_vat) }}</td>
                      <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{{ formatCurrency(plan.vat_amount) }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <!-- Monthly Trend Tab -->
              <div v-if="activeTab === 'trend'" class="overflow-x-auto">
                <h3 class="text-lg font-semibold text-gray-900 mb-4">12-Month Revenue Trend</h3>
                <table class="min-w-full divide-y divide-gray-200">
                  <thead class="bg-gray-50">
                    <tr>
                      <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Month</th>
                      <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Payments</th>
                      <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Revenue</th>
                      <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Net Revenue</th>
                      <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">VAT</th>
                    </tr>
                  </thead>
                  <tbody class="bg-white divide-y divide-gray-200">
                    <tr v-for="month in overview.monthly_trend" :key="`${month.year}-${month.month}`">
                      <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{{ month.month_name }} {{ month.year }}</td>
                      <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{{ month.payment_count }}</td>
                      <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{{ formatCurrency(month.total_revenue) }}</td>
                      <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{{ formatCurrency(month.revenue_without_vat) }}</td>
                      <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{{ formatCurrency(month.vat_amount) }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <!-- Users & Subscriptions Tab -->
              <div v-if="activeTab === 'users'">
                <div class="mb-4">
                  <div class="flex justify-between items-center mb-3">
                    <h3 class="text-lg font-semibold text-gray-900">All Users & Subscriptions</h3>
                    <p class="text-sm text-gray-500">Total Users: {{ filteredUserSubscriptions.length || 0 }}</p>
                  </div>

                  <!-- Filters -->
                  <div class="flex gap-3 items-center bg-gray-50 p-3 rounded-lg">
                    <div class="flex-1">
                      <label class="block text-xs font-medium text-gray-600 mb-1">Search User</label>
                      <input
                        v-model="userFilters.search"
                        type="text"
                        placeholder="Search by name, email, or company..."
                        class="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      />
                    </div>
                    <div>
                      <label class="block text-xs font-medium text-gray-600 mb-1">Plan</label>
                      <select
                        v-model="userFilters.plan"
                        class="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white"
                      >
                        <option value="">All Plans</option>
                        <option v-for="plan in uniquePlans" :key="plan" :value="plan">{{ plan }}</option>
                      </select>
                    </div>
                    <div>
                      <label class="block text-xs font-medium text-gray-600 mb-1">Status</label>
                      <select
                        v-model="userFilters.status"
                        class="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white"
                      >
                        <option value="">All Status</option>
                        <option value="Active">Active</option>
                        <option value="Inactive">Inactive</option>
                      </select>
                    </div>
                    <div>
                      <label class="block text-xs font-medium text-gray-600 mb-1">Registered</label>
                      <select
                        v-model="userFilters.dateRange"
                        class="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white"
                      >
                        <option value="">All Time</option>
                        <option value="today">Today</option>
                        <option value="week">This Week</option>
                        <option value="month">This Month</option>
                        <option value="quarter">This Quarter</option>
                        <option value="year">This Year</option>
                      </select>
                    </div>
                    <div class="pt-5">
                      <button
                        @click="clearUserFilters"
                        class="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-300 transition-colors"
                      >
                        Clear
                      </button>
                    </div>
                  </div>
                </div>
                <div class="overflow-x-auto">
                  <table class="min-w-full divide-y divide-gray-200">
                    <thead class="bg-gray-50">
                      <tr>
                        <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User Info</th>
                        <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contact</th>
                        <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                        <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Plan</th>
                        <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Subscription Period</th>
                        <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Days Left</th>
                        <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Paid</th>
                        <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Payments</th>
                        <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Agents Limit</th>
                      </tr>
                    </thead>
                    <tbody class="bg-white divide-y divide-gray-200">
                      <tr v-for="user in filteredUserSubscriptions" :key="user.user_id" class="table-row-hover">
                        <!-- User Info -->
                        <td class="px-4 py-4 whitespace-nowrap">
                          <div class="text-sm font-medium text-gray-900">{{ user.name }}</div>
                          <div class="text-xs text-gray-500">{{ user.company_name }}</div>
                          <div class="text-xs text-gray-400">Reg: {{ formatDate(user.registered_at) }}</div>
                        </td>

                        <!-- Contact -->
                        <td class="px-4 py-4">
                          <div class="text-xs text-gray-900">{{ user.email }}</div>
                          <div class="text-xs text-gray-500">{{ user.phone }}</div>
                        </td>

                        <!-- Status -->
                        <td class="px-4 py-4 whitespace-nowrap">
                          <span v-if="user.subscription" :class="[
                            'px-2 py-1 text-xs font-semibold rounded',
                            user.subscription.status === 'Active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          ]">
                            {{ user.subscription.status }}
                          </span>
                          <span v-else class="px-2 py-1 text-xs font-semibold rounded bg-gray-100 text-gray-600">
                            No Subscription
                          </span>
                        </td>

                        <!-- Plan -->
                        <td class="px-4 py-4 whitespace-nowrap">
                          <div v-if="user.subscription" class="text-sm">
                            <div class="font-medium text-gray-900">{{ user.subscription.plan_name }}</div>
                            <div class="text-xs text-gray-500">{{ formatCurrency(user.subscription.plan_price) }}/mo</div>
                          </div>
                          <div v-else class="text-xs text-gray-400">-</div>
                        </td>

                        <!-- Subscription Period -->
                        <td class="px-4 py-4 whitespace-nowrap">
                          <div v-if="user.subscription" class="text-xs">
                            <div class="text-gray-900">{{ formatDate(user.subscription.start_date) }}</div>
                            <div class="text-gray-500">to {{ formatDate(user.subscription.end_date) }}</div>
                          </div>
                          <div v-else class="text-xs text-gray-400">-</div>
                        </td>

                        <!-- Days Left -->
                        <td class="px-4 py-4 whitespace-nowrap">
                          <span v-if="user.subscription" :class="[
                            'text-sm font-semibold',
                            user.subscription.days_remaining < 7 ? 'text-red-600' : 'text-green-600'
                          ]">
                            {{ user.subscription.days_remaining }} days
                          </span>
                          <span v-else class="text-xs text-gray-400">-</span>
                        </td>

                        <!-- Total Paid -->
                        <td class="px-4 py-4 whitespace-nowrap">
                          <div v-if="user.payment_history" class="text-sm">
                            <div class="font-medium text-gray-900">{{ formatCurrency(user.payment_history.total_paid) }}</div>
                            <div class="text-xs text-gray-500">Net: {{ formatCurrency(user.payment_history.total_paid_without_vat) }}</div>
                          </div>
                          <div v-else class="text-xs text-gray-400">-</div>
                        </td>

                        <!-- Number of Payments -->
                        <td class="px-4 py-4 whitespace-nowrap text-center">
                          <span v-if="user.payment_history" class="text-sm font-medium text-gray-900">
                            {{ user.payment_history.total_payments }}
                          </span>
                          <span v-else class="text-xs text-gray-400">0</span>
                        </td>

                        <!-- Agents Limit -->
                        <td class="px-4 py-4 whitespace-nowrap">
                          <div v-if="user.agentLimitInfo" class="text-sm">
                            <div class="font-medium text-gray-900">
                              {{ user.agentLimitInfo.effective_limit === 0 ? 'Unlimited' : user.agentLimitInfo.effective_limit }}
                            </div>
                            <div class="text-xs" :style="user.agentLimitInfo.limit_source === 'custom' ? 'color:#7c3aed' : ''">
                              <span v-if="user.agentLimitInfo.limit_source !== 'custom'" class="text-gray-500">
                                Plan default
                              </span>
                              <span v-else>Custom</span>
                            </div>
                            <button
                              @click="openAgentLimitModal(user)"
                              class="text-xs text-indigo-600 hover:text-indigo-800 font-medium mt-0.5 block"
                            >
                              Edit
                            </button>
                          </div>
                          <div v-else class="text-sm">
                            <div class="text-gray-500 text-xs">-</div>
                            <button
                              @click="openAgentLimitModal(user)"
                              class="text-xs text-indigo-600 hover:text-indigo-800 font-medium mt-0.5 block"
                            >
                              Edit
                            </button>
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <!-- Expenses Tab -->
              <div v-if="activeTab === 'expenses'">
                <div class="flex justify-between items-center mb-4">
                  <h3 class="text-lg font-semibold text-gray-900">Business Expenses</h3>
                  <button
                    @click="openExpenseModal()"
                    class="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors flex items-center gap-2"
                  >
                    <span>+</span> Add Expense
                  </button>
                </div>

                <!-- Expense Summary Cards -->
                <div class="flex flex-wrap lg:flex-nowrap gap-3 mb-6">
                  <div class="bg-white rounded-lg shadow-sm p-4 border-l-4 border-red-600 flex-1 min-w-[200px]">
                    <h4 class="text-xs font-medium text-gray-600 mb-1">Total Monthly Expenses</h4>
                    <p class="text-xl font-bold text-gray-900">
                      {{ formatCurrency(expenseBreakdown.total_monthly_expenses || 0) }}
                    </p>
                    <p class="text-xs text-gray-500 mt-1">Active recurring costs</p>
                  </div>
                  <div class="bg-white rounded-lg shadow-sm p-4 border-l-4 border-orange-600 flex-1 min-w-[200px]">
                    <h4 class="text-xs font-medium text-gray-600 mb-1">Annual Expenses</h4>
                    <p class="text-xl font-bold text-gray-900">
                      {{ formatCurrency((expenseBreakdown.total_monthly_expenses || 0) * 12) }}
                    </p>
                    <p class="text-xs text-gray-500 mt-1">Yearly projection</p>
                  </div>
                  <div class="bg-white rounded-lg shadow-sm p-4 border-l-4 border-green-600 flex-1 min-w-[200px]">
                    <h4 class="text-xs font-medium text-gray-600 mb-1">Net Profit (Monthly)</h4>
                    <p class="text-xl font-bold" :class="(overview.mrr?.revenue_without_vat || 0) - (expenseBreakdown.total_monthly_expenses || 0) >= 0 ? 'text-green-600' : 'text-red-600'">
                      {{ formatCurrency((overview.mrr?.revenue_without_vat || 0) - (expenseBreakdown.total_monthly_expenses || 0)) }}
                    </p>
                    <p class="text-xs text-gray-500 mt-1">MRR - Expenses</p>
                  </div>
                  <div class="bg-white rounded-lg shadow-sm p-3 border-l-4 border-indigo-600 flex-1 min-w-[180px] max-w-[220px]">
                    <h4 class="text-xs font-medium text-gray-600 mb-1">WhatsApp Reminders Cost</h4>
                    <p class="text-lg font-bold text-indigo-600">
                      {{ formatCurrency(expenseBreakdown.whatsapp_reminder_costs_egp || 0) }}
                    </p>
                    <p class="text-xs text-gray-500 mt-1">Subscription reminders</p>
                  </div>
                </div>

                <!-- Expenses Table -->
                <div class="bg-white rounded-lg shadow-sm overflow-hidden">
                  <div class="overflow-x-auto">
                    <table class="min-w-full divide-y divide-gray-200">
                      <thead class="bg-gray-50">
                        <tr>
                          <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                          <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                          <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                          <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Frequency</th>
                          <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Monthly Equiv.</th>
                          <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                          <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Period</th>
                          <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                        </tr>
                      </thead>
                      <tbody class="bg-white divide-y divide-gray-200">
                        <tr v-for="expense in expenses" :key="expense.id" class="table-row-hover">
                          <td class="px-4 py-3">
                            <div class="text-sm font-medium text-gray-900">{{ expense.name }}</div>
                            <div class="text-xs text-gray-500 truncate max-w-[200px]" :title="expense.description">
                              {{ expense.description || '-' }}
                            </div>
                          </td>
                          <td class="px-4 py-3 whitespace-nowrap">
                            <span class="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800">
                              {{ expense.category || 'Uncategorized' }}
                            </span>
                          </td>
                          <td class="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                            {{ formatCurrency(expense.amount) }}
                          </td>
                          <td class="px-4 py-3 whitespace-nowrap">
                            <span :class="[
                              'px-2 py-1 text-xs font-semibold rounded',
                              expense.frequency === 'monthly' ? 'bg-blue-100 text-blue-800' :
                              expense.frequency === 'yearly' ? 'bg-purple-100 text-purple-800' :
                              'bg-gray-100 text-gray-800'
                            ]">
                              {{ expense.frequency }}
                            </span>
                          </td>
                          <td class="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                            {{ formatCurrency(calculateMonthlyEquivalent(expense)) }}
                          </td>
                          <td class="px-4 py-3 whitespace-nowrap">
                            <span :class="[
                              'px-2 py-1 text-xs font-semibold rounded',
                              expense.is_active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                            ]">
                              {{ expense.is_active ? 'Active' : 'Inactive' }}
                            </span>
                          </td>
                          <td class="px-4 py-3 whitespace-nowrap text-xs text-gray-500">
                            <div>Start: {{ formatDate(expense.start_date) }}</div>
                            <div v-if="expense.end_date">End: {{ formatDate(expense.end_date) }}</div>
                          </td>
                          <td class="px-4 py-3 whitespace-nowrap text-sm">
                            <div class="flex items-center gap-2">
                              <button
                                @click="toggleExpenseStatus(expense)"
                                :title="expense.is_active ? 'Deactivate' : 'Activate'"
                                class="text-gray-600 hover:text-indigo-600 transition-colors"
                              >
                                {{ expense.is_active ? '⏸️' : '▶️' }}
                              </button>
                              <button
                                @click="openExpenseModal(expense)"
                                title="Edit"
                                class="text-gray-600 hover:text-indigo-600 transition-colors"
                              >
                                ✏️
                              </button>
                              <button
                                @click="deleteExpense(expense)"
                                title="Delete"
                                class="text-gray-600 hover:text-red-600 transition-colors"
                              >
                                🗑️
                              </button>
                            </div>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                    <div v-if="!expenses.length" class="text-center py-12">
                      <div class="text-gray-400 text-4xl mb-2">💰</div>
                      <p class="text-gray-500">No expenses recorded yet</p>
                      <button
                        @click="openExpenseModal()"
                        class="mt-4 px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors"
                      >
                        Add Your First Expense
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Agent Limit Modal -->
              <div v-if="agentLimitModal.show" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" @click.self="closeAgentLimitModal">
                <div class="bg-white rounded-xl shadow-2xl w-full max-w-sm">
                  <div class="p-6">

                    <!-- Header -->
                    <div class="flex justify-between items-start mb-5">
                      <div>
                        <h3 class="text-base font-semibold text-gray-900">Agents Limit</h3>
                        <p class="text-xs text-gray-500 mt-0.5">{{ agentLimitModal.user.name }}</p>
                      </div>
                      <button @click="closeAgentLimitModal" class="text-gray-400 hover:text-gray-600 leading-none text-xl font-light">&times;</button>
                    </div>

                    <!-- Stats row -->
                    <div v-if="agentLimitModal.info" class="grid grid-cols-3 gap-2 mb-5">
                      <div class="bg-gray-50 rounded-lg p-2 text-center">
                        <div class="text-xs text-gray-500 mb-0.5">Plan</div>
                        <div class="text-sm font-semibold text-gray-900">{{ agentLimitModal.info.plan_limit === 0 ? '∞' : agentLimitModal.info.plan_limit }}</div>
                      </div>
                      <div class="bg-gray-50 rounded-lg p-2 text-center">
                        <div class="text-xs text-gray-500 mb-0.5">Active</div>
                        <div class="text-sm font-semibold text-gray-900">{{ agentLimitModal.info.current_agent_count }}</div>
                      </div>
                      <div class="bg-gray-50 rounded-lg p-2 text-center">
                        <div class="text-xs text-gray-500 mb-0.5">Effective</div>
                        <div class="text-sm font-semibold text-gray-900">{{ agentLimitModal.info.effective_limit === 0 ? '∞' : agentLimitModal.info.effective_limit }}</div>
                      </div>
                    </div>

                    <!-- Loading -->
                    <div v-if="agentLimitModal.loading" class="flex justify-center py-6">
                      <div class="animate-spin rounded-full h-7 w-7 border-b-2 border-indigo-600"></div>
                    </div>

                    <template v-else>
                      <!-- Toggle -->
                      <div class="flex items-center justify-between py-3 border-t border-b border-gray-200 mb-4">
                        <div>
                          <div class="text-sm font-medium text-gray-900">Custom limit</div>
                          <div class="text-xs text-gray-500">Override the plan's default</div>
                        </div>
                        <button
                          type="button"
                          @click="agentLimitModal.form.useCustom = !agentLimitModal.form.useCustom"
                          :class="['relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none', agentLimitModal.form.useCustom ? 'bg-indigo-600' : 'bg-gray-200']"
                        >
                          <span :class="['pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out', agentLimitModal.form.useCustom ? 'translate-x-5' : 'translate-x-0']"></span>
                        </button>
                      </div>

                      <!-- Custom limit input -->
                      <div v-if="agentLimitModal.form.useCustom" class="mb-4">
                        <label class="block text-sm font-medium text-gray-700 mb-1">
                          Number of agents <span class="text-gray-400 font-normal">(0 = unlimited)</span>
                        </label>
                        <input
                          v-model.number="agentLimitModal.form.agentsLimit"
                          type="number"
                          min="0"
                          placeholder="e.g. 20"
                          class="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-900 bg-white focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 focus:outline-none"
                        />
                      </div>

                      <!-- Error -->
                      <div v-if="agentLimitModal.error" class="mb-4 rounded-lg bg-red-50 border border-red-200 p-3">
                        <p class="text-xs text-red-700">{{ agentLimitModal.error }}</p>
                      </div>

                      <!-- Actions -->
                      <div class="flex gap-2 pt-1">
                        <button
                          @click="closeAgentLimitModal"
                          class="flex-1 px-4 py-2 border border-gray-200 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
                        >
                          Cancel
                        </button>
                        <button
                          @click="saveAgentLimit"
                          :disabled="agentLimitModal.saving"
                          class="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                        >
                          <span v-if="agentLimitModal.saving" class="animate-spin inline-block w-3 h-3 border-2 border-white border-t-transparent rounded-full"></span>
                          {{ agentLimitModal.saving ? 'Saving...' : 'Save' }}
                        </button>
                      </div>
                    </template>

                  </div>
                </div>
              </div>

              <!-- Expense Modal -->
              <div v-if="showExpenseModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" @click.self="closeExpenseModal">
                <div class="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                  <div class="p-6">
                    <div class="flex justify-between items-center mb-4">
                      <h3 class="text-lg font-semibold text-gray-900">
                        {{ editingExpense ? 'Edit Expense' : 'Add New Expense' }}
                      </h3>
                      <button @click="closeExpenseModal" class="text-gray-400 hover:text-gray-600 text-2xl">×</button>
                    </div>

                    <form @submit.prevent="saveExpense" class="space-y-4">
                      <div>
                        <label class="block text-sm font-medium text-gray-700 mb-1">Expense Name *</label>
                        <input
                          v-model="expenseForm.name"
                          type="text"
                          required
                          placeholder="e.g., Google Ads - Search Campaign"
                          class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                        />
                      </div>

                      <div>
                        <label class="block text-sm font-medium text-gray-700 mb-1">Description</label>
                        <textarea
                          v-model="expenseForm.description"
                          rows="2"
                          placeholder="Brief description of this expense"
                          class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                        ></textarea>
                      </div>

                      <div class="grid grid-cols-2 gap-4">
                        <div>
                          <label class="block text-sm font-medium text-gray-700 mb-1">Amount (EGP) *</label>
                          <input
                            v-model.number="expenseForm.amount"
                            type="number"
                            step="0.01"
                            min="0"
                            required
                            placeholder="0.00"
                            class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                          />
                        </div>

                        <div>
                          <label class="block text-sm font-medium text-gray-700 mb-1">Frequency *</label>
                          <select
                            v-model="expenseForm.frequency"
                            required
                            class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white"
                          >
                            <option value="monthly">Monthly</option>
                            <option value="yearly">Yearly</option>
                            <option value="one_time">One Time</option>
                          </select>
                        </div>
                      </div>

                      <div>
                        <label class="block text-sm font-medium text-gray-700 mb-1">Category</label>
                        <select
                          v-model="expenseForm.category"
                          class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white"
                        >
                          <option v-for="cat in expenseCategories" :key="cat" :value="cat">{{ cat }}</option>
                        </select>
                      </div>

                      <div class="grid grid-cols-2 gap-4">
                        <div>
                          <label class="block text-sm font-medium text-gray-700 mb-1">Start Date *</label>
                          <input
                            v-model="expenseForm.start_date"
                            type="date"
                            required
                            class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                          />
                        </div>

                        <div>
                          <label class="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                          <input
                            v-model="expenseForm.end_date"
                            type="date"
                            class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                          />
                        </div>
                      </div>

                      <div class="flex items-center">
                        <input
                          v-model="expenseForm.is_active"
                          type="checkbox"
                          :true-value="1"
                          :false-value="0"
                          class="h-4 w-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
                        />
                        <label class="ml-2 text-sm text-gray-700">Active Expense</label>
                      </div>

                      <div class="flex justify-end gap-3 mt-6 pt-4 border-t">
                        <button
                          type="button"
                          @click="closeExpenseModal"
                          class="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          class="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors"
                        >
                          {{ editingExpense ? 'Update Expense' : 'Add Expense' }}
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>

              <!-- Forecast Tab -->
              <div v-if="activeTab === 'forecast'">
                <!-- Forecast Summary Cards -->
                <div class="grid grid-cols-4 gap-4 mb-6">
                  <div class="bg-white rounded-lg shadow-sm p-4 border-l-4 border-blue-500">
                    <h3 class="text-xs font-medium text-gray-600 mb-2">Baseline MRR</h3>
                    <p class="text-2xl font-bold text-gray-900">{{ formatCurrency(forecast.baseline_mrr || 0) }}</p>
                    <p class="text-xs text-gray-500 mt-1">Current month</p>
                  </div>
                  <div class="bg-white rounded-lg shadow-sm p-4 border-l-4 border-indigo-500">
                    <h3 class="text-xs font-medium text-gray-600 mb-2">Current Subscriptions</h3>
                    <p class="text-2xl font-bold text-gray-900">{{ forecast.current_subscriptions || 0 }}</p>
                    <p class="text-xs text-gray-500 mt-1">Active now</p>
                  </div>
                  <div class="bg-white rounded-lg shadow-sm p-4 border-l-4 border-green-500">
                    <h3 class="text-xs font-medium text-gray-600 mb-2">Growth Rate</h3>
                    <p class="text-2xl font-bold text-green-600">{{ forecast.avg_growth_rate || '0%' }}</p>
                    <p class="text-xs text-gray-500 mt-1">Avg monthly</p>
                  </div>
                  <div class="bg-white rounded-lg shadow-sm p-4 border-l-4 border-red-500">
                    <h3 class="text-xs font-medium text-gray-600 mb-2">Churn Rate</h3>
                    <p class="text-2xl font-bold text-red-600">{{ forecast.avg_churn_rate || '0%' }}</p>
                    <p class="text-xs text-gray-500 mt-1">Avg monthly</p>
                  </div>
                </div>

                <h3 class="text-lg font-semibold text-gray-900 mb-4">12-Month Revenue Projection (Net Growth: {{ forecast.net_growth_rate || '0%' }})</h3>
                <div class="overflow-x-auto">
                  <table class="min-w-full divide-y divide-gray-200">
                    <thead class="bg-gray-50">
                      <tr>
                        <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Month</th>
                        <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Projected MRR</th>
                        <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Net Revenue</th>
                        <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">VAT</th>
                        <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Projected Subs</th>
                      </tr>
                    </thead>
                    <tbody class="bg-white divide-y divide-gray-200">
                      <tr v-for="(item, index) in forecast.data" :key="index">
                        <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{{ item.month }}</td>
                        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{{ formatCurrency(item.projected_revenue) }}</td>
                        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{{ formatCurrency(item.projected_revenue_without_vat) }}</td>
                        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{{ formatCurrency(item.projected_vat) }}</td>
                        <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{{ item.projected_subscriptions }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <!-- Churn Analysis Tab -->
              <div v-if="activeTab === 'churn'" class="overflow-x-auto">
                <h3 class="text-lg font-semibold text-gray-900 mb-4">12-Month Churn Analysis</h3>
                <table class="min-w-full divide-y divide-gray-200">
                  <thead class="bg-gray-50">
                    <tr>
                      <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Month</th>
                      <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Active at Start</th>
                      <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Churned</th>
                      <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Churn Rate</th>
                    </tr>
                  </thead>
                  <tbody class="bg-white divide-y divide-gray-200">
                    <tr v-for="(item, index) in churnAnalysis.data" :key="index">
                      <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{{ item.month }}</td>
                      <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{{ item.active_at_start }}</td>
                      <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{{ item.churned }}</td>
                      <td class="px-6 py-4 whitespace-nowrap text-sm">
                        <span :class="parseFloat(item.churn_rate) > 5 ? 'text-red-600 font-semibold' : 'text-green-600'">
                          {{ item.churn_rate }}
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <!-- Payment Analytics Tab -->
              <div v-if="activeTab === 'payments'">
                <div class="flex justify-between items-center mb-4">
                  <h3 class="text-lg font-semibold text-gray-900">Payment Analytics</h3>
                  <p class="text-sm text-gray-500">Total Payments: {{ paymentAnalytics.total_payments || 0 }}</p>
                </div>
                <div class="overflow-x-auto">
                  <div class="max-h-[600px] overflow-y-auto">
                    <table class="min-w-full divide-y divide-gray-200">
                      <thead class="bg-gray-50 sticky top-0 z-10">
                        <tr>
                          <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                          <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Plan</th>
                          <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                          <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                          <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Net</th>
                          <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">VAT</th>
                          <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                        </tr>
                      </thead>
                      <tbody class="bg-white divide-y divide-gray-200">
                        <tr v-for="payment in paymentAnalytics.data" :key="payment.payment_id" class="table-row-hover">
                          <td class="px-4 py-3 whitespace-nowrap">
                            <div class="text-sm font-medium text-gray-900">{{ payment.user_name }}</div>
                            <div class="text-xs text-gray-500 truncate max-w-[150px]" :title="payment.company_name">{{ payment.company_name }}</div>
                          </td>
                          <td class="px-4 py-3 whitespace-nowrap text-sm text-gray-500">{{ payment.plan_name }}</td>
                          <td class="px-4 py-3 whitespace-nowrap">
                            <span :class="[
                              'px-2 py-1 text-xs font-semibold rounded',
                              payment.status === 'COMPLETED' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                            ]">
                              {{ payment.status }}
                            </span>
                          </td>
                          <td class="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">{{ formatCurrency(payment.amount) }}</td>
                          <td class="px-4 py-3 whitespace-nowrap text-sm text-gray-500">{{ formatCurrency(payment.amount_without_vat) }}</td>
                          <td class="px-4 py-3 whitespace-nowrap text-sm text-gray-500">{{ formatCurrency(payment.vat_amount) }}</td>
                          <td class="px-4 py-3 whitespace-nowrap text-sm text-gray-500">{{ formatDate(payment.created_at) }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>

              <!-- WhatsApp Reminder Costs Tab -->
              <div v-if="activeTab === 'whatsapp-costs'" @vue:mounted="loadReminderLogs(1)">
                <!-- Summary Cards -->
                <div class="flex flex-wrap lg:flex-nowrap gap-3 mb-6">
                  <div class="bg-white rounded-lg shadow-sm p-4 border-l-4 border-indigo-600 flex-1 min-w-[200px]">
                    <h4 class="text-xs font-medium text-gray-600 mb-1">Total Messages</h4>
                    <p class="text-xl font-bold text-gray-900">{{ reminderCosts.total_messages.toLocaleString() }}</p>
                    <p class="text-xs text-gray-500 mt-1">Sent successfully</p>
                  </div>
                  <div class="bg-white rounded-lg shadow-sm p-4 border-l-4 border-green-600 flex-1 min-w-[200px]">
                    <h4 class="text-xs font-medium text-gray-600 mb-1">Total Cost (EGP)</h4>
                    <p class="text-xl font-bold text-gray-900">{{ formatCurrency(reminderCosts.total_cost_egp) }}</p>
                    <p class="text-xs text-gray-500 mt-1">Exchange rate: {{ reminderCosts.exchange_rate }} EGP/USD</p>
                  </div>
                  <div class="bg-white rounded-lg shadow-sm p-4 border-l-4 border-blue-600 flex-1 min-w-[200px]">
                    <h4 class="text-xs font-medium text-gray-600 mb-1">Total Cost (USD)</h4>
                    <p class="text-xl font-bold text-gray-900">${{ reminderCosts.total_cost_usd.toFixed(4) }}</p>
                    <p class="text-xs text-gray-500 mt-1">Original WhatsApp pricing</p>
                  </div>
                  <div class="bg-white rounded-lg shadow-sm p-4 border-l-4 border-purple-600 flex-1 min-w-[200px]">
                    <h4 class="text-xs font-medium text-gray-600 mb-1">Average Per Message</h4>
                    <p class="text-xl font-bold text-gray-900">
                      {{ reminderCosts.total_messages > 0 ? formatCurrency(reminderCosts.total_cost_egp / reminderCosts.total_messages) : '0.00 EGP' }}
                    </p>
                    <p class="text-xs text-gray-500 mt-1">Cost per message</p>
                  </div>
                </div>

                <!-- Filters -->
                <div class="bg-white rounded-lg shadow-sm p-4 mb-4">
                  <h4 class="text-sm font-semibold text-gray-900 mb-3">Filter Logs</h4>
                  <div class="flex flex-wrap lg:flex-nowrap gap-4">
                    <div class="flex-1 min-w-[200px]">
                      <label class="block text-xs font-medium text-gray-600 mb-1">Reminder Type</label>
                      <select v-model="reminderLogsFilter.reminder_type" @change="loadReminderLogs(1)" class="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500">
                        <option value="">All Types</option>
                        <option value="7_days_before">7 Days Before</option>
                        <option value="1_day_before">1 Day Before</option>
                        <option value="1_day_after">1 Day After</option>
                      </select>
                    </div>
                    <div class="flex-1 min-w-[200px]">
                      <label class="block text-xs font-medium text-gray-600 mb-1">Template Type</label>
                      <select v-model="reminderLogsFilter.template_type" @change="loadReminderLogs(1)" class="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500">
                        <option value="">All Templates</option>
                        <option value="utility">Utility</option>
                        <option value="marketing">Marketing</option>
                      </select>
                    </div>
                    <div class="flex-1 min-w-[200px]">
                      <label class="block text-xs font-medium text-gray-600 mb-1">Status</label>
                      <select v-model="reminderLogsFilter.status" @change="loadReminderLogs(1)" class="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500">
                        <option value="">All Statuses</option>
                        <option value="sent">Sent</option>
                        <option value="failed">Failed</option>
                      </select>
                    </div>
                  </div>
                </div>

                <!-- Detailed Logs Table -->
                <div class="bg-white rounded-lg shadow-sm overflow-hidden">
                  <div class="overflow-x-auto">
                    <table class="min-w-full divide-y divide-gray-200">
                      <thead class="bg-gray-50">
                        <tr>
                          <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                          <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Phone</th>
                          <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reminder Type</th>
                          <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Template</th>
                          <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cost (EGP)</th>
                          <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Cost (USD)</th>
                          <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                          <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Sent At</th>
                        </tr>
                      </thead>
                      <tbody class="bg-white divide-y divide-gray-200">
                        <tr v-for="log in reminderLogs" :key="log.id" class="table-row-hover">
                          <td class="px-4 py-3">
                            <div class="text-sm font-medium text-gray-900">{{ log.user_name }}</div>
                            <div class="text-xs text-gray-500">{{ log.user_email }}</div>
                            <div v-if="log.company_name" class="text-xs text-gray-400">{{ log.company_name }}</div>
                          </td>
                          <td class="px-4 py-3 whitespace-nowrap text-sm text-gray-900">{{ log.phone_number }}</td>
                          <td class="px-4 py-3 whitespace-nowrap">
                            <span class="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                              {{ log.reminder_type.replace(/_/g, ' ') }}
                            </span>
                          </td>
                          <td class="px-4 py-3 whitespace-nowrap">
                            <span :class="[
                              'px-2 py-1 text-xs font-semibold rounded',
                              log.template_type === 'utility' ? 'bg-green-100 text-green-800' : 'bg-purple-100 text-purple-800'
                            ]">
                              {{ log.template_type }}
                            </span>
                          </td>
                          <td class="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">{{ formatCurrency(log.cost_egp) }}</td>
                          <td class="px-4 py-3 whitespace-nowrap text-sm text-gray-600">${{ parseFloat(log.cost_usd).toFixed(4) }}</td>
                          <td class="px-4 py-3 whitespace-nowrap">
                            <span :class="[
                              'px-2 py-1 text-xs font-semibold rounded',
                              log.status === 'sent' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                            ]">
                              {{ log.status }}
                            </span>
                          </td>
                          <td class="px-4 py-3 whitespace-nowrap text-xs text-gray-500">{{ formatDate(log.sent_at) }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <!-- Pagination -->
                  <div v-if="reminderLogsPagination.total_pages > 1" class="px-4 py-3 bg-gray-50 border-t border-gray-200 flex items-center justify-between">
                    <div class="text-sm text-gray-700">
                      Page {{ reminderLogsPagination.current_page }} of {{ reminderLogsPagination.total_pages }}
                      ({{ reminderLogsPagination.total }} total messages)
                    </div>
                    <div class="flex gap-2">
                      <button
                        @click="loadReminderLogs(reminderLogsPagination.current_page - 1)"
                        :disabled="reminderLogsPagination.current_page === 1"
                        class="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Previous
                      </button>
                      <button
                        @click="loadReminderLogs(reminderLogsPagination.current_page + 1)"
                        :disabled="reminderLogsPagination.current_page === reminderLogsPagination.total_pages"
                        class="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <!-- VAT Payments Tab -->
              <div v-if="activeTab === 'vat'">
                <!-- Summary Cards -->
                <div class="grid grid-cols-4 gap-4 mb-6">
                  <!-- Due This Month -->
                  <div class="bg-white rounded-lg shadow-sm p-4 border-l-4 border-yellow-500">
                    <h3 class="text-xs font-medium text-gray-600 mb-2">Due This Month</h3>
                    <p class="text-2xl font-bold text-gray-900">
                      {{ formatCurrency(vatSummary.previous_month?.vat_collected || 0) }}
                    </p>
                    <p class="text-xs text-gray-500 mt-1" v-if="vatSummary.previous_month">
                      {{ getMonthName(vatSummary.previous_month.period_month) }} {{ vatSummary.previous_month.period_year }}
                    </p>
                    <p class="text-xs font-medium mt-1" :class="getVATStatusColor(vatSummary.previous_month?.status)">
                      {{ vatSummary.previous_month?.status || 'N/A' }}
                    </p>
                  </div>

                  <!-- Current Month Collection -->
                  <div class="bg-white rounded-lg shadow-sm p-4 border-l-4 border-blue-500">
                    <h3 class="text-xs font-medium text-gray-600 mb-2">Current Month</h3>
                    <p class="text-2xl font-bold text-gray-900">
                      {{ formatCurrency(vatSummary.current_month?.vat_collected || 0) }}
                    </p>
                    <p class="text-xs text-gray-500 mt-1" v-if="vatSummary.current_month">
                      {{ getMonthName(vatSummary.current_month.period_month) }} {{ vatSummary.current_month.period_year }}
                    </p>
                    <p class="text-xs text-gray-500 mt-1">
                      For next month payment
                    </p>
                  </div>

                  <!-- Total Unpaid -->
                  <div class="bg-white rounded-lg shadow-sm p-4 border-l-4 border-red-500">
                    <h3 class="text-xs font-medium text-gray-600 mb-2">Total Unpaid</h3>
                    <p class="text-2xl font-bold text-red-600">
                      {{ formatCurrency(vatGlobalSummary.total_due + vatGlobalSummary.total_overdue) }}
                    </p>
                    <p class="text-xs text-gray-500 mt-1">
                      Due: {{ formatCurrency(vatGlobalSummary.total_due) }}
                    </p>
                    <p class="text-xs text-red-600 mt-1">
                      Overdue: {{ formatCurrency(vatGlobalSummary.total_overdue) }}
                    </p>
                  </div>

                  <!-- Total Paid -->
                  <div class="bg-white rounded-lg shadow-sm p-4 border-l-4 border-green-500">
                    <h3 class="text-xs font-medium text-gray-600 mb-2">Total Paid</h3>
                    <p class="text-2xl font-bold text-green-600">
                      {{ formatCurrency(vatGlobalSummary.total_paid) }}
                    </p>
                    <p class="text-xs text-gray-500 mt-1">
                      All time payments
                    </p>
                  </div>
                </div>

                <!-- Actions -->
                <div class="flex justify-between items-center mb-4">
                  <h3 class="text-lg font-semibold text-gray-900">VAT Payment Records</h3>
                  <button
                    @click="openGenerateVATModal"
                    class="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors"
                  >
                    + Generate VAT Record
                  </button>
                </div>

                <!-- VAT Payments Table -->
                <div class="overflow-x-auto">
                  <div class="max-h-[600px] overflow-y-auto">
                    <table class="min-w-full divide-y divide-gray-200">
                      <thead class="bg-gray-50 sticky top-0 z-10">
                        <tr>
                          <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Period</th>
                          <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">VAT Collected</th>
                          <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total Revenue</th>
                          <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Payments</th>
                          <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                          <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Due Date</th>
                          <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Paid Date</th>
                          <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                        </tr>
                      </thead>
                      <tbody class="bg-white divide-y divide-gray-200">
                        <tr v-if="vatPayments.length === 0">
                          <td colspan="8" class="px-4 py-8 text-center text-gray-500">
                            No VAT payment records found. Click "Generate VAT Record" to create one.
                          </td>
                        </tr>
                        <tr v-for="vat in vatPayments" :key="vat.id" class="table-row-hover">
                          <td class="px-4 py-3 whitespace-nowrap">
                            <div class="text-sm font-medium text-gray-900">
                              {{ getMonthName(vat.period_month) }} {{ vat.period_year }}
                            </div>
                            <div class="text-xs text-gray-500">
                              {{ formatDate(vat.period_start_date) }} - {{ formatDate(vat.period_end_date) }}
                            </div>
                          </td>
                          <td class="px-4 py-3 whitespace-nowrap text-sm font-bold text-gray-900">
                            {{ formatCurrency(vat.vat_collected) }}
                          </td>
                          <td class="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                            {{ formatCurrency(vat.total_revenue) }}
                          </td>
                          <td class="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                            {{ vat.payment_count }}
                          </td>
                          <td class="px-4 py-3 whitespace-nowrap">
                            <span :class="[
                              'px-2 py-1 text-xs font-semibold rounded',
                              vat.status === 'PAID' ? 'bg-green-100 text-green-800' :
                              vat.status === 'OVERDUE' ? 'bg-red-100 text-red-800' :
                              'bg-yellow-100 text-yellow-800'
                            ]">
                              {{ vat.status }}
                            </span>
                          </td>
                          <td class="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                            {{ formatDate(vat.due_date) }}
                          </td>
                          <td class="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                            {{ vat.paid_date ? formatDate(vat.paid_date) : '-' }}
                          </td>
                          <td class="px-4 py-3 whitespace-nowrap text-sm">
                            <button
                              v-if="vat.status !== 'PAID'"
                              @click="confirmMarkPaid(vat)"
                              class="px-3 py-1.5 bg-green-600 text-white text-xs font-semibold rounded hover:bg-green-700 transition-colors"
                            >
                              Mark as Paid
                            </button>
                            <span v-else class="inline-flex items-center px-3 py-1.5 bg-gray-100 text-gray-600 text-xs font-semibold rounded">
                              ✓ Paid
                            </span>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>

    <!-- Mark as Paid Modal -->
    <div v-if="showMarkPaidModal" class="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
      <div class="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        <div class="p-6">
          <h3 class="text-lg font-semibold text-gray-900 mb-4">Mark VAT Payment as Paid</h3>

          <div v-if="selectedVATPayment" class="mb-4 p-3 bg-gray-50 rounded">
            <p class="text-sm text-gray-600">Period: <span class="font-semibold">{{ getMonthName(selectedVATPayment.period_month) }} {{ selectedVATPayment.period_year }}</span></p>
            <p class="text-sm text-gray-600">VAT Amount: <span class="font-semibold">{{ formatCurrency(selectedVATPayment.vat_collected) }}</span></p>
          </div>

          <form @submit.prevent="markVATAsPaid">
            <div class="mb-4">
              <label class="block text-sm font-medium text-gray-700 mb-2">Paid Amount *</label>
              <input
                v-model.number="markPaidForm.paid_amount"
                type="number"
                step="0.01"
                required
                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="0.00"
              />
            </div>

            <div class="mb-4">
              <label class="block text-sm font-medium text-gray-700 mb-2">Paid Date *</label>
              <input
                v-model="markPaidForm.paid_date"
                type="date"
                required
                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>

            <div class="mb-4">
              <label class="block text-sm font-medium text-gray-700 mb-2">Reference Number</label>
              <input
                v-model="markPaidForm.reference_number"
                type="text"
                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="GOV-2026-001234"
              />
            </div>

            <div class="mb-4">
              <label class="block text-sm font-medium text-gray-700 mb-2">Notes</label>
              <textarea
                v-model="markPaidForm.notes"
                rows="3"
                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="Payment details..."
              ></textarea>
            </div>

            <div class="flex justify-end gap-3">
              <button
                type="button"
                @click="closeMarkPaidModal"
                class="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                class="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
              >
                Mark as Paid
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>

    <!-- Generate VAT Record Modal -->
    <div v-if="showGenerateVATModal" class="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center">
      <div class="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        <div class="p-6">
          <h3 class="text-lg font-semibold text-gray-900 mb-4">Generate VAT Payment Record</h3>

          <p class="text-sm text-gray-600 mb-4">
            Generate a VAT payment record for a specific month. The system will calculate VAT from all payments made in that period.
          </p>

          <form @submit.prevent="generateVATRecord">
            <div class="mb-4">
              <label class="block text-sm font-medium text-gray-700 mb-2">Month *</label>
              <select
                v-model.number="generateVATForm.month"
                required
                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
              >
                <option :value="1">January</option>
                <option :value="2">February</option>
                <option :value="3">March</option>
                <option :value="4">April</option>
                <option :value="5">May</option>
                <option :value="6">June</option>
                <option :value="7">July</option>
                <option :value="8">August</option>
                <option :value="9">September</option>
                <option :value="10">October</option>
                <option :value="11">November</option>
                <option :value="12">December</option>
              </select>
            </div>

            <div class="mb-4">
              <label class="block text-sm font-medium text-gray-700 mb-2">Year *</label>
              <input
                v-model.number="generateVATForm.year"
                type="number"
                min="2020"
                max="2100"
                required
                class="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
              />
            </div>

            <div class="flex justify-end gap-3">
              <button
                type="button"
                @click="closeGenerateVATModal"
                class="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                class="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
              >
                Generate Record
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Sidebar from '@/components/Sidebar.vue'
import TopHeader from '@/components/TopHeader.vue'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  LineController,
  BarController,
  DoughnutController,
  Title,
  Tooltip,
  Legend
} from 'chart.js'

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  LineController,
  BarController,
  DoughnutController,
  Title,
  Tooltip,
  Legend
)

export default {
  name: 'AdminFinancialDashboardView',
  components: {
    Sidebar,
    TopHeader
  },
  data () {
    return {
      loading: true,
      error: null,
      activeTab: 'overview',
      dateFilter: 'all',
      customStartDate: '',
      customEndDate: '',
      overview: {},
      userSubscriptions: { data: [], total_users: 0 },
      paymentAnalytics: { data: [], total_payments: 0 },
      forecast: { data: [] },
      churnAnalysis: { data: [] },
      userFilters: {
        search: '',
        plan: '',
        status: '',
        dateRange: ''
      },
      agentLimitModal: {
        show: false,
        loading: false,
        saving: false,
        error: null,
        user: {},
        info: null,
        form: {
          useCustom: false,
          agentsLimit: 0
        }
      },
      charts: {
        revenueTrend: null,
        revenuePlan: null,
        mrr: null,
        arr: null,
        subscriptionGrowth: null,
        whatsappMessages: null
      },
      whatsappStats: {
        today_messages: 0,
        week_total: 0,
        daily_stats: []
      },
      subscriptionGrowth: {
        data: []
      },
      tabs: [
        { id: 'overview', label: 'Overview' },
        { id: 'plans', label: 'Revenue by Plan' },
        { id: 'trend', label: 'Monthly Trend' },
        { id: 'users', label: 'Users & Subscriptions' },
        { id: 'expenses', label: 'Expenses' },
        { id: 'whatsapp-costs', label: 'WhatsApp Costs' },
        { id: 'vat', label: 'VAT Payments' },
        { id: 'forecast', label: 'Forecast' },
        { id: 'churn', label: 'Churn Analysis' },
        { id: 'payments', label: 'Payment Analytics' }
      ],
      expenses: [],
      expenseBreakdown: {},
      showExpenseModal: false,
      expenseForm: {
        name: '',
        description: '',
        amount: 0,
        frequency: 'monthly',
        category: 'Marketing',
        is_active: 1,
        start_date: new Date().toISOString().split('T')[0],
        end_date: null
      },
      editingExpense: null,
      expenseCategories: ['Marketing', 'Infrastructure', 'Software', 'Operations', 'Other'],
      // WhatsApp Reminder Costs
      reminderCosts: {
        total_messages: 0,
        total_cost_usd: 0,
        total_cost_egp: 0,
        exchange_rate: 51.50,
        by_template_type: [],
        by_reminder_type: [],
        by_country: []
      },
      reminderLogs: [],
      initializingInstances: false,
      reminderLogsPagination: {
        current_page: 1,
        per_page: 50,
        total: 0,
        total_pages: 0
      },
      reminderLogsFilter: {
        reminder_type: '',
        template_type: '',
        status: 'sent'
      },
      // VAT Payments
      vatPayments: [],
      vatSummary: {
        current_month: null,
        previous_month: null,
        unpaid_records: [],
        total_unpaid: 0
      },
      vatGlobalSummary: {
        total_due: 0,
        total_overdue: 0,
        total_paid: 0
      },
      showMarkPaidModal: false,
      showGenerateVATModal: false,
      selectedVATPayment: null,
      markPaidForm: {
        paid_amount: 0,
        paid_date: new Date().toISOString().split('T')[0],
        reference_number: '',
        notes: ''
      },
      generateVATForm: {
        month: new Date().getMonth() === 0 ? 12 : new Date().getMonth(),
        year: new Date().getMonth() === 0 ? new Date().getFullYear() - 1 : new Date().getFullYear()
      }
    }
  },
  computed: {
    uniquePlans () {
      const plans = new Set()
      this.userSubscriptions.data.forEach(user => {
        if (user.subscription && user.subscription.plan_name) {
          plans.add(user.subscription.plan_name)
        }
      })
      return Array.from(plans).sort()
    },
    filteredUserSubscriptions () {
      let filtered = [...this.userSubscriptions.data]

      // Search filter
      if (this.userFilters.search) {
        const search = this.userFilters.search.toLowerCase()
        filtered = filtered.filter(user =>
          user.name?.toLowerCase().includes(search) ||
          user.email?.toLowerCase().includes(search) ||
          user.company_name?.toLowerCase().includes(search)
        )
      }

      // Plan filter
      if (this.userFilters.plan) {
        filtered = filtered.filter(user =>
          user.subscription && user.subscription.plan_name === this.userFilters.plan
        )
      }

      // Status filter
      if (this.userFilters.status) {
        filtered = filtered.filter(user =>
          user.subscription && user.subscription.status === this.userFilters.status
        )
      }

      // Date range filter
      if (this.userFilters.dateRange && this.userFilters.dateRange !== '') {
        const today = new Date()
        let startDate = null

        switch (this.userFilters.dateRange) {
          case 'today':
            startDate = new Date(today.setHours(0, 0, 0, 0))
            break
          case 'week': {
            const weekStart = new Date(today)
            weekStart.setDate(today.getDate() - today.getDay())
            startDate = weekStart
            break
          }
          case 'month':
            startDate = new Date(today.getFullYear(), today.getMonth(), 1)
            break
          case 'quarter': {
            const quarterStart = new Date(today.getFullYear(), Math.floor(today.getMonth() / 3) * 3, 1)
            startDate = quarterStart
            break
          }
          case 'year':
            startDate = new Date(today.getFullYear(), 0, 1)
            break
        }

        if (startDate) {
          filtered = filtered.filter(user => {
            if (!user.registered_at) return false
            const regDate = new Date(user.registered_at)
            return regDate >= startDate
          })
        }
      }

      return filtered
    }
  },
  mounted () {
    this.checkAdminAccess()

    // Read tab from URL parameter
    const urlParams = new URLSearchParams(window.location.search)
    const tabParam = urlParams.get('tab')
    if (tabParam && this.tabs.some(t => t.id === tabParam)) {
      this.activeTab = tabParam
    }

    this.loadAllData()
  },
  beforeUnmount () {
    // Clean up charts to prevent memory leaks
    try {
      if (this.charts.revenueTrend) {
        this.charts.revenueTrend.destroy()
        this.charts.revenueTrend = null
      }
      if (this.charts.revenuePlan) {
        this.charts.revenuePlan.destroy()
        this.charts.revenuePlan = null
      }
      if (this.charts.mrr) {
        this.charts.mrr.destroy()
        this.charts.mrr = null
      }
      if (this.charts.arr) {
        this.charts.arr.destroy()
        this.charts.arr = null
      }
      if (this.charts.subscriptionGrowth) {
        this.charts.subscriptionGrowth.destroy()
        this.charts.subscriptionGrowth = null
      }
      if (this.charts.whatsappMessages) {
        this.charts.whatsappMessages.destroy()
        this.charts.whatsappMessages = null
      }
    } catch (error) {
      console.error('Error cleaning up charts:', error)
    }
  },
  watch: {
    activeTab (newTab, oldTab) {
      // Update URL parameter when tab changes
      const url = new URL(window.location)
      url.searchParams.set('tab', newTab)
      window.history.pushState({}, '', url)

      if (newTab === 'whatsapp-costs' && newTab !== oldTab) {
        this.loadReminderLogs(1)
      }
      if (newTab === 'expenses' && this.expenses.length === 0) {
        this.loadExpenses()
      }
      if (newTab === 'vat' && this.vatPayments.length === 0) {
        this.loadVATPayments()
      }
    },
    overview: {
      handler (newVal) {
        if (newVal && Object.keys(newVal).length > 0) {
          this.$nextTick(() => {
            this.renderCharts()
          })
        }
      },
      deep: true
    },
    forecast: {
      handler (newVal) {
        if (newVal && newVal.data && newVal.data.length > 0) {
          this.$nextTick(() => {
            this.renderSubscriptionGrowthChart()
          })
        }
      },
      deep: true
    }
  },
  methods: {
    checkAdminAccess () {
      const user = JSON.parse(localStorage.getItem('user') || '{}')
      if (user.role_id !== 1) {
        this.$router.push('/')
        this.error = 'Access denied. Admin only.'
      }
    },
    clearUserFilters () {
      this.userFilters = {
        search: '',
        plan: '',
        status: '',
        dateRange: ''
      }
    },

    async openAgentLimitModal (user) {
      this.agentLimitModal.show = true
      this.agentLimitModal.loading = false
      this.agentLimitModal.error = null
      this.agentLimitModal.user = user
      this.agentLimitModal.form = { useCustom: false, agentsLimit: 0 }

      // Use pre-loaded data from the users list response
      if (user.agentLimitInfo) {
        const info = user.agentLimitInfo
        this.agentLimitModal.info = info
        this.agentLimitModal.form.useCustom = info.custom_agents_limit === 1
        this.agentLimitModal.form.agentsLimit = info.custom_limit !== null ? info.custom_limit : 0
        return
      }

      // Fallback: fetch if somehow missing
      this.agentLimitModal.loading = true
      try {
        const token = localStorage.getItem('token')
        const res = await fetch(`https://api.quali-d.com/admin/agents/custom-limit/${user.user_id}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
        const data = await res.json()
        if (!res.ok) throw new Error(data.message || 'Failed to load limit info')

        this.agentLimitModal.info = data
        this.agentLimitModal.form.useCustom = data.custom_agents_limit === 1
        this.agentLimitModal.form.agentsLimit = data.custom_limit !== null ? data.custom_limit : 0
        user.agentLimitInfo = data
      } catch (err) {
        this.agentLimitModal.error = err.message
      } finally {
        this.agentLimitModal.loading = false
      }
    },

    closeAgentLimitModal () {
      this.agentLimitModal.show = false
    },

    async saveAgentLimit () {
      this.agentLimitModal.saving = true
      this.agentLimitModal.error = null

      const { user, form } = this.agentLimitModal
      const body = {
        target_user_id: user.user_id,
        custom_agents_limit: form.useCustom ? 1 : 0
      }
      if (form.useCustom) body.agents_limit = form.agentsLimit

      try {
        const token = localStorage.getItem('token')
        const res = await fetch('https://api.quali-d.com/admin/agents/set-custom-limit', {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify(body)
        })
        const data = await res.json()
        if (!res.ok) throw new Error(data.message || 'Failed to save')

        // Update the cached badge on the user row
        user.agentLimitInfo = {
          ...this.agentLimitModal.info,
          custom_agents_limit: body.custom_agents_limit,
          custom_limit: form.useCustom ? form.agentsLimit : null,
          effective_limit: form.useCustom ? form.agentsLimit : (this.agentLimitModal.info ? this.agentLimitModal.info.plan_limit : null),
          limit_source: form.useCustom ? 'custom' : 'plan (default)'
        }

        this.closeAgentLimitModal()
      } catch (err) {
        this.agentLimitModal.error = err.message
      } finally {
        this.agentLimitModal.saving = false
      }
    },
    applyDateFilter () {
      // Date filter logic will be applied when making API calls
      // For now, just reload data
      this.loadAllData()
    },
    getDateRangeDisplay () {
      const filters = {
        today: 'Today',
        week: 'This Week',
        month: 'This Month',
        quarter: 'This Quarter',
        year: 'This Year',
        custom: `${this.customStartDate || 'N/A'} to ${this.customEndDate || 'N/A'}`
      }
      return filters[this.dateFilter] || 'All Time'
    },
    getDateRangeParams () {
      const today = new Date()
      let startDate = null
      let endDate = null

      switch (this.dateFilter) {
        case 'today':
          startDate = today.toISOString().split('T')[0]
          endDate = startDate
          break
        case 'week': {
          const weekStart = new Date(today)
          weekStart.setDate(today.getDate() - today.getDay())
          startDate = weekStart.toISOString().split('T')[0]
          endDate = today.toISOString().split('T')[0]
          break
        }
        case 'month':
          startDate = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0]
          endDate = today.toISOString().split('T')[0]
          break
        case 'quarter': {
          const quarterStart = new Date(today.getFullYear(), Math.floor(today.getMonth() / 3) * 3, 1)
          startDate = quarterStart.toISOString().split('T')[0]
          endDate = today.toISOString().split('T')[0]
          break
        }
        case 'year':
          startDate = new Date(today.getFullYear(), 0, 1).toISOString().split('T')[0]
          endDate = today.toISOString().split('T')[0]
          break
        case 'custom':
          startDate = this.customStartDate
          endDate = this.customEndDate
          break
      }

      return { start_date: startDate, end_date: endDate }
    },
    async loadAllData () {
      this.loading = true
      this.error = null

      try {
        const token = localStorage.getItem('token')
        const headers = {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
          Accept: 'application/json'
        }

        // Get date range parameters
        const dateParams = this.getDateRangeParams()
        const queryString = dateParams.start_date && dateParams.end_date
          ? `?start_date=${dateParams.start_date}&end_date=${dateParams.end_date}`
          : ''

        // Load all data in parallel (including expenses, whatsapp stats, subscription growth, and reminder costs)
        const [overviewRes, usersRes, paymentsRes, forecastRes, churnRes, expensesRes, breakdownRes, whatsappRes, subscriptionGrowthRes, reminderCostsRes] = await Promise.all([
          fetch(`https://api.quali-d.com/admin/financial/overview${queryString}`, { headers, mode: 'cors' }),
          fetch(`https://api.quali-d.com/admin/financial/users-subscriptions${queryString}`, { headers, mode: 'cors' }),
          fetch(`https://api.quali-d.com/admin/financial/payment-analytics${queryString}`, { headers, mode: 'cors' }),
          fetch(`https://api.quali-d.com/admin/financial/forecast${queryString}`, { headers, mode: 'cors' }),
          fetch(`https://api.quali-d.com/admin/financial/churn-analysis${queryString}`, { headers, mode: 'cors' }),
          fetch('https://api.quali-d.com/admin/financial/expenses', { headers, mode: 'cors' }),
          fetch('https://api.quali-d.com/admin/financial/expenses/breakdown', { headers, mode: 'cors' }),
          fetch('https://api.quali-d.com/admin/financial/whatsapp-messages', { headers, mode: 'cors' }),
          fetch('https://api.quali-d.com/admin/financial/subscription-growth', { headers, mode: 'cors' }),
          fetch(`https://api.quali-d.com/admin/financial/subscription-reminder-costs${queryString}`, { headers, mode: 'cors' })
        ])

        // Check for errors
        if (!overviewRes.ok || !usersRes.ok || !paymentsRes.ok || !forecastRes.ok || !churnRes.ok) {
          if (overviewRes.status === 403 || usersRes.status === 403) {
            this.error = 'Access denied. Admin only.'
            setTimeout(() => {
              this.$router.push('/')
            }, 2000)
            return
          }
          throw new Error('Failed to load financial data')
        }

        // Parse JSON responses
        const [overviewData, usersData, paymentsData, forecastData, churnData, expensesData, breakdownData, whatsappData, subscriptionGrowthData, reminderCostsData] = await Promise.all([
          overviewRes.json(),
          usersRes.json(),
          paymentsRes.json(),
          forecastRes.json(),
          churnRes.json(),
          expensesRes.ok ? expensesRes.json() : Promise.resolve({ data: [] }),
          breakdownRes.ok ? breakdownRes.json() : Promise.resolve({}),
          whatsappRes.ok ? whatsappRes.json() : Promise.resolve({ data: { today_messages: 0, week_total: 0, daily_stats: [] } }),
          subscriptionGrowthRes.ok ? subscriptionGrowthRes.json() : Promise.resolve({ data: [] }),
          reminderCostsRes.ok ? reminderCostsRes.json() : Promise.resolve({ data: { total_messages: 0, total_cost_usd: 0, total_cost_egp: 0, exchange_rate: 51.50, by_template_type: [], by_reminder_type: [], by_country: [] } })
        ])

        this.overview = overviewData.data || {}
        // Map agent_limit_info (snake_case from API) to agentLimitInfo (camelCase for template)
        if (usersData && usersData.data) {
          usersData.data = usersData.data.map(u => ({
            ...u,
            agentLimitInfo: u.agent_limit_info || null
          }))
        }
        this.userSubscriptions = usersData || { data: [], total_users: 0 }
        this.paymentAnalytics = paymentsData || { data: [], total_payments: 0 }
        this.forecast = forecastData || { data: [] }
        this.churnAnalysis = churnData || { data: [] }
        this.expenses = expensesData.data || []
        this.expenseBreakdown = breakdownData || {}
        this.whatsappStats = whatsappData.data || { today_messages: 0, week_total: 0, daily_stats: [] }
        this.subscriptionGrowth = subscriptionGrowthData || { data: [] }
        this.reminderCosts = reminderCostsData.data || { total_messages: 0, total_cost_usd: 0, total_cost_egp: 0, exchange_rate: 51.50, by_template_type: [], by_reminder_type: [], by_country: [] }

        // Render charts after data is loaded
        this.$nextTick(() => {
          this.renderCharts()
        })
      } catch (err) {
        console.error('Error loading financial data:', err)
        this.error = err.message || 'Failed to load financial data. Please try again.'
      } finally {
        this.loading = false
      }
    },
    formatCurrency (amount) {
      return new Intl.NumberFormat('en-EG', {
        style: 'currency',
        currency: 'EGP',
        minimumFractionDigits: 2
      }).format(amount || 0)
    },
    formatDate (dateString) {
      if (!dateString) return '-'
      const date = new Date(dateString)
      return new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      }).format(date)
    },
    getPlanColor (index) {
      const colors = [
        '#4f46e5',
        '#10b981',
        '#f59e0b',
        '#ef4444',
        '#8b5cf6',
        '#ec4899'
      ]
      return colors[index % colors.length]
    },
    renderCharts () {
      // Only render if we're on the overview tab
      if (this.activeTab !== 'overview') {
        console.log('Not on overview tab, skipping chart render')
        return
      }

      // Wait for next tick to ensure DOM is ready
      this.$nextTick(() => {
        console.log('Starting to render all charts...')
        console.log('Overview data:', this.overview)
        console.log('Forecast data:', this.forecast)

        // Small delay to ensure canvas elements are fully rendered
        setTimeout(() => {
          this.renderRevenueTrendChart()
          this.renderRevenuePlanChart()
          this.renderMrrChart()
          this.renderArrChart()
          this.renderSubscriptionGrowthChart()
          this.renderWhatsAppMessagesChart()
        }, 200)
      })
    },
    renderRevenueTrendChart () {
      if (!this.$refs.revenueTrendChart) {
        console.warn('revenueTrendChart ref not found')
        return
      }

      const monthlyData = this.overview.monthly_trend || []
      if (!monthlyData.length) {
        console.warn('No monthly trend data available')
        return
      }

      const labels = monthlyData.map(m => `${m.month_name} ${m.year}`)
      const revenueData = monthlyData.map(m => m.total_revenue)
      const netRevenueData = monthlyData.map(m => m.revenue_without_vat)

      // Destroy existing chart if it exists
      if (this.charts.revenueTrend) {
        try {
          this.charts.revenueTrend.destroy()
        } catch (e) {
          console.warn('Error destroying revenue trend chart:', e)
        }
        this.charts.revenueTrend = null
      }

      try {
        const canvas = this.$refs.revenueTrendChart
        const ctx = canvas.getContext('2d')

        if (!ctx) {
          console.error('Failed to get 2d context for revenue trend chart')
          return
        }

        this.charts.revenueTrend = new ChartJS(ctx, {
          type: 'line',
          data: {
            labels,
            datasets: [
              {
                label: 'Total Revenue',
                data: revenueData,
                borderColor: '#4f46e5',
                backgroundColor: 'rgba(79, 70, 229, 0.1)',
                tension: 0.4,
                fill: true
              },
              {
                label: 'Net Revenue',
                data: netRevenueData,
                borderColor: '#10b981',
                backgroundColor: 'rgba(16, 185, 129, 0.1)',
                tension: 0.4,
                fill: true
              }
            ]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            animation: false,
            plugins: {
              legend: {
                position: 'bottom'
              },
              tooltip: {
                callbacks: {
                  label: (context) => {
                    const value = context.parsed.y
                    return `${context.dataset.label}: ${this.formatCurrency(value)}`
                  }
                }
              }
            },
            scales: {
              y: {
                beginAtZero: true,
                ticks: {
                  callback: (value) => this.formatCurrency(value)
                }
              }
            }
          }
        })
        console.log('Revenue trend chart created successfully')
      } catch (error) {
        console.error('Error creating revenue trend chart:', error)
      }
    },
    renderRevenuePlanChart () {
      if (!this.$refs.revenuePlanChart) {
        console.warn('revenuePlanChart ref not found')
        return
      }

      const planData = this.overview.revenue_by_plan || []
      if (!planData.length) {
        console.warn('No revenue by plan data available')
        return
      }

      const labels = planData.map(p => p.plan_name)
      const data = planData.map(p => p.total_revenue)

      // Destroy existing chart if it exists
      if (this.charts.revenuePlan) {
        try {
          this.charts.revenuePlan.destroy()
        } catch (e) {
          console.warn('Error destroying revenue plan chart:', e)
        }
        this.charts.revenuePlan = null
      }

      try {
        const canvas = this.$refs.revenuePlanChart
        const ctx = canvas.getContext('2d')

        if (!ctx) {
          console.error('Failed to get 2d context for revenue plan chart')
          return
        }

        this.charts.revenuePlan = new ChartJS(ctx, {
          type: 'doughnut',
          data: {
            labels,
            datasets: [{
              data,
              backgroundColor: [
                '#4f46e5',
                '#10b981',
                '#f59e0b',
                '#ef4444',
                '#8b5cf6',
                '#ec4899'
              ],
              borderWidth: 2,
              borderColor: '#fff'
            }]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            animation: false,
            plugins: {
              legend: {
                display: false
              },
              tooltip: {
                callbacks: {
                  label: (context) => {
                    const value = context.parsed
                    const total = context.dataset.data.reduce((a, b) => a + b, 0)
                    const percentage = ((value / total) * 100).toFixed(1)
                    const planData = this.overview.revenue_by_plan[context.dataIndex]
                    return [
                      `${context.label}`,
                      `Total Revenue: ${this.formatCurrency(value)}`,
                      `Payments: ${planData.payment_count}`,
                      `Share: ${percentage}%`
                    ]
                  }
                }
              }
            }
          }
        })
        console.log('Revenue plan chart created successfully')
      } catch (error) {
        console.error('Error creating revenue plan chart:', error)
      }
    },
    renderMrrChart () {
      if (!this.$refs.mrrChart) {
        console.warn('mrrChart ref not found')
        return
      }

      const mrr = this.overview.mrr?.total || 0
      const mrrNet = this.overview.mrr?.revenue_without_vat || 0
      const mrrVat = this.overview.mrr?.vat_amount || 0

      // Destroy existing chart if it exists
      if (this.charts.mrr) {
        try {
          this.charts.mrr.destroy()
        } catch (e) {
          console.warn('Error destroying MRR chart:', e)
        }
        this.charts.mrr = null
      }

      try {
        const canvas = this.$refs.mrrChart
        const ctx = canvas.getContext('2d')

        if (!ctx) {
          console.error('Failed to get 2d context for MRR chart')
          return
        }

        this.charts.mrr = new ChartJS(ctx, {
          type: 'doughnut',
          data: {
            labels: ['Net Revenue', 'VAT (14%)'],
            datasets: [{
              data: [mrrNet, mrrVat],
              backgroundColor: ['#4f46e5', '#10b981'],
              borderWidth: 2,
              borderColor: '#fff'
            }]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            animation: false,
            plugins: {
              legend: {
                position: 'bottom'
              },
              tooltip: {
                callbacks: {
                  label: (context) => {
                    const value = context.parsed
                    const total = mrr
                    const percentage = ((value / total) * 100).toFixed(1)
                    return `${context.label}: ${this.formatCurrency(value)} (${percentage}%)`
                  }
                }
              }
            }
          }
        })
        console.log('MRR chart created successfully')
      } catch (error) {
        console.error('Error creating MRR chart:', error)
      }
    },
    renderArrChart () {
      if (!this.$refs.arrChart) {
        console.warn('arrChart ref not found')
        return
      }

      const arr = this.overview.arr?.total || 0
      const arrNet = this.overview.arr?.revenue_without_vat || 0
      const arrVat = this.overview.arr?.vat_amount || 0

      // Destroy existing chart if it exists
      if (this.charts.arr) {
        try {
          this.charts.arr.destroy()
        } catch (e) {
          console.warn('Error destroying ARR chart:', e)
        }
        this.charts.arr = null
      }

      try {
        const canvas = this.$refs.arrChart
        const ctx = canvas.getContext('2d')

        if (!ctx) {
          console.error('Failed to get 2d context for ARR chart')
          return
        }

        this.charts.arr = new ChartJS(ctx, {
          type: 'doughnut',
          data: {
            labels: ['Net Revenue', 'VAT (14%)'],
            datasets: [{
              data: [arrNet, arrVat],
              backgroundColor: ['#10b981', '#f59e0b'],
              borderWidth: 2,
              borderColor: '#fff'
            }]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            animation: false,
            plugins: {
              legend: {
                position: 'bottom'
              },
              tooltip: {
                callbacks: {
                  label: (context) => {
                    const value = context.parsed
                    const total = arr
                    const percentage = ((value / total) * 100).toFixed(1)
                    return `${context.label}: ${this.formatCurrency(value)} (${percentage}%)`
                  }
                }
              }
            }
          }
        })
        console.log('ARR chart created successfully')
      } catch (error) {
        console.error('Error creating ARR chart:', error)
      }
    },
    renderSubscriptionGrowthChart () {
      if (!this.$refs.subscriptionGrowthChart) {
        console.warn('subscriptionGrowthChart ref not found')
        return
      }

      const growthData = this.subscriptionGrowth.data || []
      if (!growthData.length) {
        console.warn('No subscription growth data available')
        return
      }

      const labels = growthData.map(g => g.month)
      const data = growthData.map(g => g.active_subscriptions)

      // Destroy existing chart if it exists
      if (this.charts.subscriptionGrowth) {
        try {
          this.charts.subscriptionGrowth.destroy()
        } catch (e) {
          console.warn('Error destroying subscription growth chart:', e)
        }
        this.charts.subscriptionGrowth = null
      }

      try {
        const canvas = this.$refs.subscriptionGrowthChart
        const ctx = canvas.getContext('2d')

        if (!ctx) {
          console.error('Failed to get 2d context for subscription growth chart')
          return
        }

        this.charts.subscriptionGrowth = new ChartJS(ctx, {
          type: 'line',
          data: {
            labels,
            datasets: [{
              label: 'Active Subscriptions',
              data,
              borderColor: '#8b5cf6',
              backgroundColor: 'rgba(139, 92, 246, 0.1)',
              tension: 0.4,
              fill: true,
              pointRadius: 4,
              pointHoverRadius: 6
            }]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            animation: false,
            plugins: {
              legend: {
                position: 'bottom'
              }
            },
            scales: {
              y: {
                beginAtZero: true,
                ticks: {
                  stepSize: 1
                }
              }
            }
          }
        })
        console.log('Subscription growth chart created successfully')
      } catch (error) {
        console.error('Error creating subscription growth chart:', error)
      }
    },
    async loadExpenses () {
      try {
        const token = localStorage.getItem('token')
        const headers = {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
          Accept: 'application/json'
        }

        const [expensesRes, breakdownRes] = await Promise.all([
          fetch('https://api.quali-d.com/admin/financial/expenses', { headers, mode: 'cors' }),
          fetch('https://api.quali-d.com/admin/financial/expenses/breakdown', { headers, mode: 'cors' })
        ])

        if (expensesRes.ok) {
          const data = await expensesRes.json()
          this.expenses = data.data || []
        }

        if (breakdownRes.ok) {
          const breakdown = await breakdownRes.json()
          this.expenseBreakdown = breakdown || {}
        }
      } catch (err) {
        console.error('Error loading expenses:', err)
      }
    },
    calculateMonthlyEquivalent (expense) {
      if (expense.frequency === 'monthly') {
        return expense.amount
      } else if (expense.frequency === 'yearly') {
        return expense.amount / 12
      } else if (expense.frequency === 'one_time') {
        return 0
      }
      return 0
    },
    openExpenseModal (expense = null) {
      if (expense) {
        this.editingExpense = expense
        this.expenseForm = {
          name: expense.name,
          description: expense.description || '',
          amount: expense.amount,
          frequency: expense.frequency,
          category: expense.category || 'Marketing',
          is_active: expense.is_active,
          start_date: expense.start_date,
          end_date: expense.end_date || null
        }
      } else {
        this.editingExpense = null
        this.expenseForm = {
          name: '',
          description: '',
          amount: 0,
          frequency: 'monthly',
          category: 'Marketing',
          is_active: 1,
          start_date: new Date().toISOString().split('T')[0],
          end_date: null
        }
      }
      this.showExpenseModal = true
    },
    closeExpenseModal () {
      this.showExpenseModal = false
      this.editingExpense = null
      this.expenseForm = {
        name: '',
        description: '',
        amount: 0,
        frequency: 'monthly',
        category: 'Marketing',
        is_active: 1,
        start_date: new Date().toISOString().split('T')[0],
        end_date: null
      }
    },
    async saveExpense () {
      try {
        const token = localStorage.getItem('token')
        const headers = {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
          Accept: 'application/json'
        }

        const url = this.editingExpense
          ? `https://api.quali-d.com/admin/financial/expenses/${this.editingExpense.id}`
          : 'https://api.quali-d.com/admin/financial/expenses'

        const method = this.editingExpense ? 'PUT' : 'POST'

        const response = await fetch(url, {
          method,
          headers,
          mode: 'cors',
          body: JSON.stringify(this.expenseForm)
        })

        const data = await response.json()

        if (data.success) {
          await this.loadExpenses()
          this.closeExpenseModal()
        } else {
          alert(data.message || 'Failed to save expense')
        }
      } catch (err) {
        console.error('Error saving expense:', err)
        alert('Failed to save expense. Please try again.')
      }
    },
    async toggleExpenseStatus (expense) {
      if (!confirm(`Are you sure you want to ${expense.is_active ? 'deactivate' : 'activate'} this expense?`)) {
        return
      }

      try {
        const token = localStorage.getItem('token')
        const headers = {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
          Accept: 'application/json'
        }

        const response = await fetch(`https://api.quali-d.com/admin/financial/expenses/${expense.id}/toggle-status`, {
          method: 'PATCH',
          headers,
          mode: 'cors'
        })

        const data = await response.json()

        if (data.success) {
          await this.loadExpenses()
        } else {
          alert(data.message || 'Failed to update expense status')
        }
      } catch (err) {
        console.error('Error toggling expense status:', err)
        alert('Failed to update expense status. Please try again.')
      }
    },
    async deleteExpense (expense) {
      if (!confirm(`Are you sure you want to delete "${expense.name}"? This action cannot be undone.`)) {
        return
      }

      try {
        const token = localStorage.getItem('token')
        const headers = {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
          Accept: 'application/json'
        }

        const response = await fetch(`https://api.quali-d.com/admin/financial/expenses/${expense.id}`, {
          method: 'DELETE',
          headers,
          mode: 'cors'
        })

        const data = await response.json()

        if (data.success) {
          await this.loadExpenses()
        } else {
          alert(data.message || 'Failed to delete expense')
        }
      } catch (err) {
        console.error('Error deleting expense:', err)
        alert('Failed to delete expense. Please try again.')
      }
    },
    renderWhatsAppMessagesChart () {
      if (!this.$refs.whatsappMessagesChart) {
        console.warn('whatsappMessagesChart ref not found')
        return
      }

      const dailyStats = this.whatsappStats.daily_stats || []
      if (!dailyStats.length) {
        console.warn('No WhatsApp message stats available')
        return
      }

      const labels = dailyStats.map(d => d.day_name)
      const data = dailyStats.map(d => d.message_count)

      // Destroy existing chart if it exists
      if (this.charts.whatsappMessages) {
        try {
          this.charts.whatsappMessages.destroy()
        } catch (e) {
          console.warn('Error destroying WhatsApp messages chart:', e)
        }
        this.charts.whatsappMessages = null
      }

      try {
        const canvas = this.$refs.whatsappMessagesChart
        const ctx = canvas.getContext('2d')

        if (!ctx) {
          console.error('Failed to get 2d context for WhatsApp messages chart')
          return
        }

        this.charts.whatsappMessages = new ChartJS(ctx, {
          type: 'bar',
          data: {
            labels,
            datasets: [{
              label: 'Messages',
              data,
              backgroundColor: 'rgba(37, 211, 102, 0.5)',
              borderColor: '#25D366',
              borderWidth: 2
            }]
          },
          options: {
            responsive: true,
            maintainAspectRatio: false,
            animation: false,
            plugins: {
              legend: {
                position: 'bottom'
              },
              tooltip: {
                callbacks: {
                  label: (context) => {
                    const value = context.parsed.y
                    const date = dailyStats[context.dataIndex].date
                    return [`${value} messages`, `Date: ${date}`]
                  }
                }
              }
            },
            scales: {
              y: {
                beginAtZero: true,
                ticks: {
                  stepSize: 1
                }
              }
            }
          }
        })
        console.log('WhatsApp messages chart created successfully')
      } catch (error) {
        console.error('Error creating WhatsApp messages chart:', error)
      }
    },
    calculateProfitMargin () {
      const revenue = this.overview.mrr?.revenue_without_vat || 0
      const expenses = this.expenseBreakdown.total_monthly_expenses || 0

      if (revenue === 0) return 0

      const profit = revenue - expenses
      const margin = (profit / revenue) * 100

      return margin.toFixed(1)
    },
    // VAT Payment Methods
    async loadVATPayments () {
      try {
        const token = localStorage.getItem('token')
        const headers = {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
          Accept: 'application/json'
        }

        const [paymentsRes, summaryRes] = await Promise.all([
          fetch('https://api.quali-d.com/admin/financial/vat-payments', { headers, mode: 'cors' }),
          fetch('https://api.quali-d.com/admin/financial/vat-payments/summary', { headers, mode: 'cors' })
        ])

        if (paymentsRes.ok) {
          const paymentsData = await paymentsRes.json()
          this.vatPayments = paymentsData.data || []
          this.vatGlobalSummary = paymentsData.summary || {
            total_due: 0,
            total_overdue: 0,
            total_paid: 0
          }
        }

        if (summaryRes.ok) {
          const summaryData = await summaryRes.json()
          this.vatSummary = summaryData.data || {
            current_month: null,
            previous_month: null,
            unpaid_records: [],
            total_unpaid: 0
          }
        }
      } catch (err) {
        console.error('Error loading VAT payments:', err)
      }
    },
    getMonthName (month) {
      const months = ['January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December']
      return months[month - 1] || ''
    },
    getVATStatusColor (status) {
      if (status === 'PAID') return 'text-green-600'
      if (status === 'OVERDUE') return 'text-red-600'
      if (status === 'DUE') return 'text-yellow-600'
      return 'text-gray-600'
    },
    confirmMarkPaid (vatPayment) {
      const monthName = this.getMonthName(vatPayment.period_month)
      const amount = this.formatCurrency(vatPayment.vat_collected)
      const dueDate = this.formatDate(vatPayment.due_date)

      const confirmed = confirm(
        'Mark ' + monthName + ' ' + vatPayment.period_year + ' VAT payment as PAID?\n\n' +
        'Amount: ' + amount + '\n' +
        'Due Date: ' + dueDate + '\n\n' +
        'Click OK to proceed with payment details.'
      )

      if (confirmed) {
        this.openMarkPaidModal(vatPayment)
      }
    },
    openMarkPaidModal (vatPayment) {
      this.selectedVATPayment = vatPayment
      this.markPaidForm = {
        paid_amount: vatPayment.vat_collected,
        paid_date: new Date().toISOString().split('T')[0],
        reference_number: '',
        notes: ''
      }
      this.showMarkPaidModal = true
    },
    closeMarkPaidModal () {
      this.showMarkPaidModal = false
      this.selectedVATPayment = null
      this.markPaidForm = {
        paid_amount: 0,
        paid_date: new Date().toISOString().split('T')[0],
        reference_number: '',
        notes: ''
      }
    },
    async markVATAsPaid () {
      try {
        const token = localStorage.getItem('token')
        const headers = {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
          Accept: 'application/json'
        }

        const response = await fetch(`https://api.quali-d.com/admin/financial/vat-payments/${this.selectedVATPayment.id}/mark-paid`, {
          method: 'POST',
          headers,
          mode: 'cors',
          body: JSON.stringify(this.markPaidForm)
        })

        const data = await response.json()

        if (data.success) {
          alert('VAT payment marked as paid successfully!')
          this.closeMarkPaidModal()
          await this.loadVATPayments()
        } else {
          alert(`Error: ${data.message}`)
        }
      } catch (err) {
        console.error('Error marking VAT as paid:', err)
        alert('Failed to mark VAT payment as paid. Please try again.')
      }
    },
    openGenerateVATModal () {
      const now = new Date()
      const currentMonth = now.getMonth() + 1
      const currentYear = now.getFullYear()

      // Default to previous month
      this.generateVATForm = {
        month: currentMonth === 1 ? 12 : currentMonth - 1,
        year: currentMonth === 1 ? currentYear - 1 : currentYear
      }
      this.showGenerateVATModal = true
    },
    closeGenerateVATModal () {
      this.showGenerateVATModal = false
    },
    async generateVATRecord () {
      try {
        const token = localStorage.getItem('token')
        const headers = {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
          Accept: 'application/json'
        }

        const response = await fetch('https://api.quali-d.com/admin/financial/vat-payments/generate', {
          method: 'POST',
          headers,
          mode: 'cors',
          body: JSON.stringify(this.generateVATForm)
        })

        const data = await response.json()

        if (data.success) {
          alert(`VAT record generated successfully!\nVAT Collected: ${this.formatCurrency(data.data.vat_collected)}`)
          this.closeGenerateVATModal()
          await this.loadVATPayments()
        } else {
          alert(`Error: ${data.message}`)
        }
      } catch (err) {
        console.error('Error generating VAT record:', err)
        alert('Failed to generate VAT record. Please try again.')
      }
    },
    async loadReminderLogs (page = 1) {
      try {
        const token = localStorage.getItem('token')
        const headers = {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
          Accept: 'application/json'
        }

        const params = new URLSearchParams({
          page: page,
          per_page: this.reminderLogsPagination.per_page
        })

        if (this.reminderLogsFilter.reminder_type) {
          params.append('reminder_type', this.reminderLogsFilter.reminder_type)
        }
        if (this.reminderLogsFilter.template_type) {
          params.append('template_type', this.reminderLogsFilter.template_type)
        }
        if (this.reminderLogsFilter.status) {
          params.append('status', this.reminderLogsFilter.status)
        }

        const dateParams = this.getDateRangeParams()
        if (dateParams.start_date && dateParams.end_date) {
          params.append('start_date', dateParams.start_date)
          params.append('end_date', dateParams.end_date)
        }

        const response = await fetch(`https://api.quali-d.com/admin/financial/subscription-reminder-logs?${params.toString()}`, {
          headers,
          mode: 'cors'
        })

        if (response.ok) {
          const data = await response.json()
          this.reminderLogs = data.data || []
          this.reminderLogsPagination = data.pagination || {
            current_page: 1,
            per_page: 50,
            total: 0,
            total_pages: 0
          }
        }
      } catch (err) {
        console.error('Error loading reminder logs:', err)
      }
    },
    async forceInitializeAllInstances () {
      // Confirm action with user
      const confirmed = confirm(
        '⚠️ Force Initialize All WhatsApp Instances?\n\n' +
        'This will attempt to reinitialize ALL active WhatsApp sessions in the database.\n' +
        'This may take a few moments depending on the number of instances.\n\n' +
        'Do you want to continue?'
      )

      if (!confirmed) {
        return
      }

      this.initializingInstances = true

      try {
        const token = localStorage.getItem('token')
        const response = await fetch('https://api.quali-d.com/whatsapp/force-initialize-all', {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })

        const data = await response.json()

        if (data.success) {
          // Show detailed results
          const resultsMessage = '✅ Initialization Complete!\n\n' +
            `Total Instances: ${data.total}\n` +
            `Successful: ${data.successful}\n` +
            `Failed: ${data.failed}\n\n` +
            (data.failed > 0 ? 'Check console for detailed results.' : 'All instances initialized successfully!')

          alert(resultsMessage)

          // Log detailed results to console
          console.log('Force Initialize Results:', data.results)

          // Show toast notification
          this.$toast.success(`Successfully initialized ${data.successful} out of ${data.total} instances`)
        } else {
          alert(`❌ Error: ${data.message}`)
          this.$toast.error(data.message || 'Failed to initialize instances')
        }
      } catch (err) {
        console.error('Error force initializing instances:', err)
        alert(`❌ Error: ${err.message}`)
        this.$toast.error('Failed to initialize WhatsApp instances')
      } finally {
        this.initializingInstances = false
      }
    }
  }
}
</script>

<style scoped>
/* Dashboard Layout */
.dashboard-layout {
  display: flex;
  min-height: 100vh;
  background: var(--bg-primary, #f9fafb);
}

.main-content {
  flex: 1;
  margin-left: 280px;
  transition: margin-left 0.3s ease;
}

.page-content {
  padding: 0;
  background: var(--bg-primary, #f9fafb);
  min-height: calc(100vh - 80px);
}

/* Override Tailwind classes for dark mode support */
.bg-gray-50 {
  background-color: var(--bg-primary, #f9fafb) !important;
}

.bg-white {
  background-color: var(--bg-secondary, #ffffff) !important;
}

.text-gray-900 {
  color: var(--text-primary, #111827) !important;
}

.text-gray-600 {
  color: var(--text-secondary, #4b5563) !important;
}

.text-gray-500 {
  color: var(--text-tertiary, #6b7280) !important;
}

.text-gray-400 {
  color: var(--text-tertiary, #9ca3af) !important;
}

.text-gray-700 {
  color: var(--text-secondary, #374151) !important;
}

.text-red-600 {
  color: #dc2626 !important;
}

.text-red-800 {
  color: #991b1b !important;
}

.text-green-600 {
  color: #16a34a !important;
}

.text-green-800 {
  color: #166534 !important;
}

.border-gray-200 {
  border-color: var(--border-primary, #e5e7eb) !important;
}

.divide-gray-200 > :not([hidden]) ~ :not([hidden]) {
  border-color: var(--border-primary, #e5e7eb) !important;
}

/* Table header */
.bg-gray-50 {
  background-color: var(--bg-tertiary, #f9fafb) !important;
}

/* Cards and shadows */
.shadow-md {
  box-shadow: 0 4px 6px -1px var(--shadow-light, rgba(0, 0, 0, 0.1)), 0 2px 4px -1px var(--shadow-light, rgba(0, 0, 0, 0.06)) !important;
}

/* Loading spinner */
.border-indigo-600 {
  border-color: #4f46e5 !important;
}

/* Custom scrollbar for tables */
.overflow-y-auto::-webkit-scrollbar {
  width: 6px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background: var(--bg-tertiary, #f1f1f1);
  border-radius: 3px;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background: var(--text-tertiary, #888);
  border-radius: 3px;
}

.overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background: var(--text-secondary, #555);
}

/* Tabs container */
.tabs-container {
  background-color: var(--bg-tertiary, #f3f4f6);
}

/* Tab items */
.tab-item {
  color: var(--text-secondary, #6b7280);
}

.tab-item:hover {
  color: var(--text-primary, #111827);
  background-color: var(--bg-secondary, rgba(255, 255, 255, 0.5));
}

.tab-item.active {
  background-color: var(--bg-secondary, #ffffff);
  color: #4f46e5;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
}

/* Table row hover effects */
.table-row-hover {
  transition: background-color 0.15s ease-in-out;
}

.table-row-hover:hover {
  background-color: var(--bg-tertiary, #f9fafb) !important;
}

/* Responsive adjustments */
@media (max-width: 1024px) {
  .main-content {
    margin-left: 0;
  }
}
</style>
